/**
 * Dynamic Read More - Progressive Enhancement
 *
 * Adds collapsible functionality to card excerpts that are taller than 3 lines (~72px)
 * Fully accessible with proper ARIA attributes and keyboard support
 */

(function () {
	'use strict';

	// Configuration
	const CONFIG = {
		getMaxHeight: (element) => {
			// Get the --excerpt-lines CSS variable value
			const excerptLines = getComputedStyle(element).getPropertyValue('--excerpt-lines').trim();
			const lines = parseInt(excerptLines) || 4; // fallback to 4 lines

			// Calculate approximate height: line-height * number of lines
			const computedStyle = getComputedStyle(element);
			const lineHeight = parseFloat(computedStyle.lineHeight) || 24; // fallback to 24px

			return lines * lineHeight;
		},
		selectors: {
			cardContainer: '.item-card--js-read-more',
			excerptContainer: '.item-card__excerpt',
			buttonContainer: '.js-adds-dynamic-read-more',
			imageContainer: '.item-card__image',
		},
		classes: {
			collapsed: 'collapsed',
			button: 'read-more-toggle',
			srOnly: 'sr-only',
		},
		text: {
			showMore: 'Read more',
			showLess: 'Read less',
			expanded: 'Full description expanded',
			collapsed: 'Description collapsed to summary',
		},
	};

	let resizeTimer;

	/**
	 * Initialize the dynamic read-more functionality
	 */
	function init() {
		// Find all share-offer cards with button containers
		const cards = document.querySelectorAll(CONFIG.selectors.cardContainer);

		if (!cards.length) {
			return;
		}

		// Process each card
		cards.forEach(processCard);

		// Handle window resize with debouncing
		window.addEventListener('resize', debounce(handleResize, 250));
	}

	/**
	 * Process a single card for read-more functionality
	 * @param {Element} card - The card element
	 */
	function processCard(card) {
		const excerptEl = card.querySelector(CONFIG.selectors.excerptContainer);
		const buttonContainer = card.querySelector(CONFIG.selectors.buttonContainer);

		if (!excerptEl || !buttonContainer) {
			return;
		}

		// Create unique IDs for ARIA relationships
		const cardId = card.id || `card-${Math.random().toString(36).substr(2, 9)}`;
		const excerptId = `${cardId}-excerpt`;
		const buttonId = `${cardId}-toggle`;
		const announceId = `${cardId}-announce`;

		// Set IDs for ARIA relationships
		excerptEl.id = excerptId;

		// Check if content needs collapsing
		if (shouldCollapse(excerptEl)) {
			setupCollapsibleCard(card, excerptEl, buttonContainer, {
				excerptId,
				buttonId,
				announceId,
			});
		}
	}

	/**
	 * Check if the excerpt content should be collapsed
	 * @param {Element} excerptEl - The excerpt element
	 * @returns {boolean} Whether the content should be collapsed
	 */
	function shouldCollapse(excerptEl) {
		// Temporarily remove any existing collapsed class to get natural height
		const wasCollapsed = excerptEl
			.closest(CONFIG.selectors.cardContainer)
			.classList.contains(CONFIG.classes.collapsed);

		if (wasCollapsed) {
			excerptEl.closest(CONFIG.selectors.cardContainer).classList.remove(CONFIG.classes.collapsed);
		}

		const height = excerptEl.scrollHeight;
		const maxHeight = CONFIG.getMaxHeight(excerptEl);

		// Restore collapsed state if it was there
		if (wasCollapsed) {
			excerptEl.closest(CONFIG.selectors.cardContainer).classList.add(CONFIG.classes.collapsed);
		}

		return height > maxHeight;
	}

	/**
	 * Set up a card with collapsible functionality
	 * @param {Element} card - The card element
	 * @param {Element} excerptEl - The excerpt element
	 * @param {Element} buttonContainer - The button container element
	 * @param {Object} ids - Object containing element IDs
	 */
	function setupCollapsibleCard(card, excerptEl, buttonContainer, ids) {
		// Add collapsed class initially
		card.classList.add(CONFIG.classes.collapsed);

		// Create the toggle button
		const button = createToggleButton(ids, false); // Start collapsed

		// Create announcement region for screen readers
		const announceEl = createAnnouncementRegion(ids.announceId);

		// Move the entire read-more container before the excerpt for better tab order
		const readMoreContainer = buttonContainer.closest('.item-card__read-more');
		if (readMoreContainer) {
			excerptEl.parentNode.insertBefore(readMoreContainer, excerptEl);
			// Add class for CSS-based visual repositioning
			readMoreContainer.classList.add('visually-after-content');
			excerptEl.classList.add('has-read-more-container');
		}

		// Add the button and announcement to the container
		buttonContainer.appendChild(button);
		buttonContainer.appendChild(announceEl);

		// Hide collapsed content from assistive technology
		if (card.classList.contains(CONFIG.classes.collapsed)) {
			setContentAccessibility(excerptEl, true); // true = collapsed
		}

		// Add event listener
		button.addEventListener('click', () => {
			toggleContent(card, button, announceEl);
		});

		// Add keyboard support
		button.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				toggleContent(card, button, announceEl);
			}
		});
	}

	/**
	 * Create the toggle button element
	 * @param {Object} ids - Object containing element IDs
	 * @param {boolean} isExpanded - Whether content is currently expanded
	 * @returns {Element} The button element
	 */
	function createToggleButton(ids, isExpanded) {
		const button = document.createElement('button');
		button.type = 'button';
		button.id = ids.buttonId;
		button.className = CONFIG.classes.button;
		button.setAttribute('aria-expanded', isExpanded.toString());
		button.setAttribute('aria-controls', ids.excerptId);

		// Create button content with text and SVG icon
		const buttonText = isExpanded ? CONFIG.text.showLess : CONFIG.text.showMore;
		const svgIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M9.0007 14.791V3.62102L13.8807 8.50102C14.2707 8.89102 14.9107 8.89102 15.3007 8.50102C15.6907 8.11102 15.6907 7.48102 15.3007 7.09102L8.7107 0.501015C8.3207 0.111015 7.6907 0.111015 7.3007 0.501015L0.700703 7.08102C0.310703 7.47102 0.310703 8.10102 0.700703 8.49102C1.0907 8.88102 1.7207 8.88102 2.1107 8.49102L7.0007 3.62102L7.0007 14.791C7.0007 15.341 7.4507 15.791 8.0007 15.791C8.5507 15.791 9.0007 15.341 9.0007 14.791Z" fill="#165D34"/>
</svg>`;

		button.innerHTML = `${buttonText} ${svgIcon}`;

		return button;
	}

	/**
	 * Create announcement region for screen readers
	 * @param {string} announceId - The ID for the announcement region
	 * @returns {Element} The announcement element
	 */
	function createAnnouncementRegion(announceId) {
		const announceEl = document.createElement('div');
		announceEl.id = announceId;
		announceEl.className = CONFIG.classes.srOnly;
		announceEl.setAttribute('aria-live', 'polite');
		announceEl.setAttribute('aria-atomic', 'true');

		return announceEl;
	}

	/**
	 * Set content accessibility based on collapsed state
	 * @param {Element} excerptEl - The excerpt element
	 * @param {boolean} isCollapsed - Whether content is collapsed
	 */
	function setContentAccessibility(excerptEl, isCollapsed) {
		const links = excerptEl.querySelectorAll('a, button, [tabindex]');

		if (isCollapsed) {
			// Hide overflow content from screen readers
			excerptEl.setAttribute('aria-hidden', 'true');
			// Remove interactive elements from tab order
			links.forEach((link) => {
				link.setAttribute('tabindex', '-1');
				link.setAttribute('aria-hidden', 'true');
			});
		} else {
			// Make content accessible again
			excerptEl.removeAttribute('aria-hidden');
			// Restore interactive elements to tab order
			links.forEach((link) => {
				link.removeAttribute('tabindex');
				link.removeAttribute('aria-hidden');
			});
		}
	}

	/**
	 * Toggle the collapsed state of the content
	 * @param {Element} card - The card element
	 * @param {Element} button - The toggle button
	 * @param {Element} announceEl - The announcement element
	 */
	function toggleContent(card, button, announceEl) {
		const isCurrentlyCollapsed = card.classList.contains(CONFIG.classes.collapsed);
		const willExpand = isCurrentlyCollapsed;
		const excerptEl = card.querySelector(CONFIG.selectors.excerptContainer);
		const imageEl = card.querySelector(CONFIG.selectors.imageContainer);

		if (willExpand) {
			// Before expanding, preserve the current image height to prevent distortion
			if (imageEl && !imageEl.style.maxHeight) {
				const currentImageHeight = imageEl.offsetHeight;
				imageEl.style.maxHeight = currentImageHeight + 'px';
			}

			// Expanding: First set a specific height for transition, then remove max-height after
			const naturalHeight = excerptEl.scrollHeight;

			excerptEl.style.maxHeight = Math.max(naturalHeight, 1200) + 'px';

			// Listen for transition end to remove max-height constraint
			const handleTransitionEnd = (e) => {
				if (e.target === excerptEl && e.propertyName === 'max-height') {
					excerptEl.style.maxHeight = 'none';
					excerptEl.removeEventListener('transitionend', handleTransitionEnd);
				}
			};
			excerptEl.addEventListener('transitionend', handleTransitionEnd);
		} else {
			// Collapsing: First set specific height, then add collapsed class
			const naturalHeight = excerptEl.scrollHeight;
			excerptEl.style.maxHeight = naturalHeight + 'px';

			// Force reflow then collapse
			excerptEl.offsetHeight;
			excerptEl.style.maxHeight = '';

			// Scroll card into view after collapse starts for better UX
			setTimeout(() => {
				card.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
					inline: 'nearest',
				});
			}, 50); // Small delay to let transition start
		}

		// Toggle the collapsed class
		card.classList.toggle(CONFIG.classes.collapsed);

		// Update content accessibility
		setContentAccessibility(excerptEl, !willExpand);

		// Update button state
		button.setAttribute('aria-expanded', willExpand.toString());
		const buttonText = willExpand ? CONFIG.text.showLess : CONFIG.text.showMore;
		const svgIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M9.0007 14.791V3.62102L13.8807 8.50102C14.2707 8.89102 14.9107 8.89102 15.3007 8.50102C15.6907 8.11102 15.6907 7.48102 15.3007 7.09102L8.7107 0.501015C8.3207 0.111015 7.6907 0.111015 7.3007 0.501015L0.700703 7.08102C0.310703 7.47102 0.310703 8.10102 0.700703 8.49102C1.0907 8.88102 1.7207 8.88102 2.1107 8.49102L7.0007 3.62102L7.0007 14.791C7.0007 15.341 7.4507 15.791 8.0007 15.791C8.5507 15.791 9.0007 15.341 9.0007 14.791Z" fill="#165D34"/>
</svg>`;
		button.innerHTML = `${buttonText} ${svgIcon}`;

		// Announce the change to screen readers
		announceEl.textContent = willExpand ? CONFIG.text.expanded : CONFIG.text.collapsed;

		// Clear the announcement after a delay so it can be reused
		setTimeout(() => {
			announceEl.textContent = '';
		}, 1000);
	}

	/**
	 * Handle window resize events
	 */
	function handleResize() {
		// Re-check all cards on resize
		const cards = document.querySelectorAll(CONFIG.selectors.cardContainer);
		cards.forEach((card) => {
			const excerptEl = card.querySelector(CONFIG.selectors.excerptContainer);
			const buttonContainer = card.querySelector(CONFIG.selectors.buttonContainer);
			const existingButton = card.querySelector(`.${CONFIG.classes.button}`);

			if (!excerptEl || !buttonContainer) {
				return;
			}

			if (shouldCollapse(excerptEl)) {
				// Content should be collapsible
				if (!existingButton) {
					// No button exists, create the collapsible setup
					processCard(card);
				}
			} else {
				// Content doesn't need to be collapsible
				if (existingButton) {
					// Remove existing button and collapsed state
					card.classList.remove(CONFIG.classes.collapsed);
					setContentAccessibility(excerptEl, false); // Make content fully accessible

					// Move read-more container back to its original position (after excerpt)
					const readMoreContainer = card.querySelector('.item-card__read-more');
					if (readMoreContainer && excerptEl.nextSibling !== readMoreContainer) {
						excerptEl.parentNode.insertBefore(readMoreContainer, excerptEl.nextSibling);
					}

					// Remove CSS classes for visual positioning
					if (readMoreContainer) {
						readMoreContainer.classList.remove('visually-after-content');
					}
					excerptEl.classList.remove('has-read-more-container');

					// Remove the dynamic button and announcement
					existingButton.remove();
					const announceEl = card.querySelector(`[id$="-announce"]`);
					if (announceEl) {
						announceEl.remove();
					}
				}
			}
		});
	}

	/**
	 * Debounce function to limit how often a function can be called
	 * @param {Function} func - Function to debounce
	 * @param {number} wait - Wait time in milliseconds
	 * @returns {Function} Debounced function
	 */
	function debounce(func, wait) {
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(resizeTimer);
				func(...args);
			};
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(later, wait);
		};
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
